import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, ShieldAlert } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking, isAppAdmin } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { GuestContractsPanel } from "@/components/guests/GuestContractsPanel";
import { GuestMailPanel } from "@/components/guests/GuestMailPanel";
import { GuestOverviewPanel } from "@/components/guests/GuestOverviewPanel";
import { GuestStayHistory } from "@/components/guests/GuestStayHistory";
import { EditGuestModal } from "@/pages/guests/EditGuestModal";
import { GuestPrivacyErasureModal } from "@/pages/guests/GuestPrivacyErasureModal";
import type { Guest } from "@/types/camping";

export function GuestDetailPage() {
  const { guestId } = useParams<{ guestId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const canErase = isAppAdmin(groups);

  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);

  const loadGuest = useCallback(() => {
    if (!guestId) return;
    setLoading(true);
    setError(null);
    api
      .get<Guest>(`/api/v1/guests/${guestId}`)
      .then(setGuest)
      .catch((err: unknown) => {
        setGuest(null);
        setError(err instanceof Error ? err.message : "Gast konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [guestId]);

  useEffect(() => {
    loadGuest();
  }, [loadGuest]);

  useEffect(() => {
    if (searchParams.get("edit") === "1" && guest && canEdit) {
      setEditOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [canEdit, guest, searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("erase") === "1" && guest && canErase && !guest.privacy_erased_at) {
      setEraseOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [canErase, guest, searchParams, setSearchParams]);

  useEffect(() => {
    if (!guest) return;
    setPageMeta({
      title: `${guest.last_name}, ${guest.first_name}`,
      subtitle: "Gastübersicht",
    });
    return () => setPageMeta(null);
  }, [guest, setPageMeta]);

  if (!guestId) {
    return (
      <p className="text-sm text-status-red">Ungültige Gast-ID.</p>
    );
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Gast wird geladen…</p>;
  }

  if (error || !guest) {
    return (
      <div className="space-y-4">
        <Link
          to="/guests"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
        >
          <ArrowLeft size={16} aria-hidden />
          Zurück zur Stammkartei
        </Link>
        <p className="text-sm text-status-red">{error ?? "Gast nicht gefunden."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditGuestModal
        open={editOpen}
        guest={guest}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          loadGuest();
          setEditOpen(false);
        }}
        canEdit={canEdit && !guest.privacy_erased_at}
      />
      <GuestPrivacyErasureModal
        open={eraseOpen}
        guest={guest}
        onClose={() => setEraseOpen(false)}
        onErased={(erased) => setGuest(erased)}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to="/guests"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
        >
          <ArrowLeft size={16} aria-hidden />
          Zurück zur Stammkartei
        </Link>
        <div className="flex flex-wrap gap-2">
          {canErase && !guest.privacy_erased_at ? (
            <Button type="button" variant="danger" onClick={() => setEraseOpen(true)}>
              <ShieldAlert size={16} className="mr-1.5 inline" aria-hidden />
              Daten löschen (DSGVO)
            </Button>
          ) : null}
          {canEdit && !guest.privacy_erased_at ? (
            <Button type="button" onClick={() => setEditOpen(true)}>
              <Pencil size={16} className="mr-1.5 inline" aria-hidden />
              Daten bearbeiten
            </Button>
          ) : null}
        </div>
      </div>

      <GuestOverviewPanel guest={guest} />
      <GuestStayHistory guestId={guest.id} active />
      <GuestContractsPanel guestId={guest.id} active />
      <GuestMailPanel guest={guest} active />
      <AuditHistory entityType="guest" entityId={guest.id} active limit={50} />
    </div>
  );
}
